import { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { buildTree, flattenTree, getDescendantKeys, type TreeItem } from "@/lib/tree-utils";

export interface ParentPickableItem extends TreeItem {
  _key: string;
  name: string;
  icon: string;
  parent_key?: string | null;
}

interface ParentPickerProps {
  /** All available items */
  items: ParentPickableItem[];
  /** Currently selected parent key ("" or null = no parent) */
  value: string;
  /** Called when selection changes */
  onChange: (key: string) => void;
  /** Key of the item being edited (excluded from list along with its descendants) */
  excludeKey?: string;
  /** Placeholder when nothing selected */
  placeholder?: string;
}

export function ParentPicker({
  items,
  value,
  onChange,
  excludeKey,
  placeholder = "None (top-level)",
}: ParentPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setSearch("");
    }
  }, [open]);

  // Exclude self and descendants
  const excludedKeys = useMemo(() => {
    if (!excludeKey) return new Set<string>();
    const desc = getDescendantKeys(items, excludeKey);
    desc.add(excludeKey);
    return desc;
  }, [items, excludeKey]);

  // Build tree-sorted list
  const sortedItems = useMemo(() => {
    const available = items.filter(i => !excludedKeys.has(i._key));
    const tree = buildTree(available);
    const flat = flattenTree(tree, new Set());
    return flat.map(f => ({ ...f.item, _depth: f.depth }));
  }, [items, excludedKeys]);

  // Filter by search
  const filteredItems = useMemo(() => {
    if (!search.trim()) return sortedItems;
    const q = search.toLowerCase();
    return sortedItems.filter(i => i.name.toLowerCase().includes(q));
  }, [sortedItems, search]);

  // Display label for trigger
  const selectedItem = useMemo(
    () => items.find(i => i._key === value),
    [items, value],
  );

  const handleSelect = (key: string) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full h-9 px-3 py-2 text-sm rounded-md border border-input bg-transparent shadow-xs transition-colors hover:bg-app-surface-raised/50"
      >
        <span className={selectedItem ? "text-app-text" : "text-app-text-muted"}>
          {selectedItem ? (
            <span className="flex items-center gap-1.5">
              <span>{selectedItem.icon}</span>
              <span className="truncate">{selectedItem.name}</span>
            </span>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-70 w-full bottom-full mb-1 rounded-lg border border-app-border bg-app-surface shadow-lg flex flex-col max-h-52">
          {/* Search */}
          <div className="relative shrink-0 p-1.5 border-b border-app-border">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-text-muted" />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-8 h-8 text-sm"
            />
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1">
            {/* None option */}
            <button
              type="button"
              onClick={() => handleSelect("")}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-app-accent/10 transition-colors text-sm ${
                !value ? "bg-app-accent/5 text-app-accent font-medium" : "text-app-text"
              }`}
            >
              <span className="truncate">{placeholder}</span>
              {!value && <span className="ml-auto text-app-accent shrink-0">✓</span>}
            </button>

            {filteredItems.length === 0 ? (
              <p className="p-3 text-info text-app-text-muted text-center">No items found</p>
            ) : (
              filteredItems.map(item => {
                const isSelected = item._key === value;
                return (
                  <button
                    key={item._key}
                    type="button"
                    onClick={() => handleSelect(item._key)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-app-accent/10 transition-colors text-sm ${
                      isSelected ? "bg-app-accent/5 text-app-accent font-medium" : "text-app-text"
                    }`}
                  >
                    {item._depth > 0 && (
                      <span className="text-app-text-muted shrink-0">{"─".repeat(item._depth)}</span>
                    )}
                    <span className="shrink-0">{item.icon}</span>
                    <span className="truncate">{item.name}</span>
                    {isSelected && <span className="ml-auto text-app-accent shrink-0">✓</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
